import React from 'react';
import { Card, CardContent, Typography, Stack } from '@mui/material';

export const SummaryCard = ({ 
  data, 
  field, 
  title,
  aggregationType = 'sum',
  showSign = true, 
  formatDecimals = 2 ,
  conditionalFormat = true
}) => {
  // Aggregation functions
  const aggregationFunctions = {
    sum: (arr) => arr.reduce((sum, item) => sum + (item[field] || 0), 0),
    count: (arr) => arr.length,
    min: (arr) => Math.min(...arr.map(item => item[field] || 0)),
    max: (arr) => Math.max(...arr.map(item => item[field] || 0)),
    average: (arr) => {
      const validItems = arr.filter(item => item[field] != null);
      return validItems.length > 0 
        ? validItems.reduce((sum, item) => sum + item[field], 0) / validItems.length
        : 0;
    }
  };

  // Calculate total based on selected aggregation type
  const total = aggregationFunctions[aggregationType] 
    ? aggregationFunctions[aggregationType](data)
    : 0;
  
  // Format number to specified decimal places
  const formattedTotal = total.toFixed(formatDecimals);
  
  // Determine if positive value for styling (for sum, average, max)
  const isPositive = 
    ['sum', 'average', 'max'].includes(aggregationType) 
      ? total >= 0 
      : false;
  
  // Generate a descriptive title if not provided
  const cardTitle = title || `${aggregationType.charAt(0).toUpperCase() + aggregationType.slice(1)} ${field}`;

  return (
    <Card sx={{ minWidth: 180, maxWidth: 200 }}>
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Typography variant="body2" component="div" gutterBottom color="text.secondary">
          {cardTitle}
        </Typography>
        <Typography 
          variant="h5" 
          component="div"
          sx={{ 
            fontWeight: 'bold',
            color: showSign ? ((isPositive & conditionalFormat) ? 'success.main' : 'error.main') : 'text.primary'
          }}
        >
          {showSign && isPositive ? '+' : ''}{formattedTotal}
        </Typography>
      </CardContent>
    </Card>
  );
};

export const MetricCard = ({ title, value, subtitle, color = 'text.primary' }) => (
  <Card sx={{ minWidth: 180, maxWidth: 200 }}>
    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
      <Typography variant="body2" component="div" gutterBottom color="text.secondary">
        {title}
      </Typography>
      <Typography variant="h5" component="div" sx={{ fontWeight: 'bold', color }}>
        {value}
      </Typography>
      {subtitle && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>{subtitle}</Typography>
      )}
    </CardContent>
  </Card>
);
